import { useFetch, useNotificationContext } from "@4uhub/lib4uhub";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoIcon from "@mui/icons-material/Info";
import ReplayIcon from "@mui/icons-material/Replay";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { format } from "date-fns";
import { ptBR as dateFnsPtBR, enUS, es } from "date-fns/locale";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import TableList from "../../../../components/TableList/TableList";
import {
  IIndividualBeneficiaryRefundListData,
  IIndividualBeneficiaryRefundMediaFileListData,
} from "../../../../models/individual-beneficiary-refund";
import {
  getIndividualBeneficiaryRefundLogs,
  getIndividualBeneficiaryRefundMediaFiles,
  retryRefundMediaFileIntegration,
} from "../../../../services/individualBeneficiaryRefund.service";

interface IRefundDetailsModalProps {
  refundData: IIndividualBeneficiaryRefundListData;
  onClose: () => void;
}

const translationPath = "page.4uBus.logs.refund.details.";

const ERRO_CODE = "4";

const statusColors: Record<
  string,
  "warning" | "info" | "success" | "error" | "default"
> = {
  "1": "warning", // Pendente
  "2": "info", // Processando
  "3": "success", // Processado
  "4": "error", // Erro
  "5": "error", // Erro_EmailEnviado
  "6": "warning", // RetentativaManual
};

const RefundDetailsModal: React.FC<IRefundDetailsModalProps> = ({
  refundData,
  onClose,
}) => {
  const { t, i18n } = useTranslation();
  const [logData, setLogData] = useState<any[]>([]);
  const [selectedLogData, setSelectedLogData] = useState<string>("");
  const [showLogModal, setShowLogModal] = useState(false);
  const [mediaFiles, setMediaFiles] = useState<
    IIndividualBeneficiaryRefundMediaFileListData[]
  >([]);
  const [expandedMediaFileId, setExpandedMediaFileId] = useState<
    string | false
  >(false);
  const [mediaFileLogs, setMediaFileLogs] = useState<Record<string, any[]>>(
    {},
  );

  const { setMessage } = useNotificationContext();
  const { sendRequest: retryMediaFileRequest } = useFetch(
    retryRefundMediaFileIntegration,
  );

  const fetchMediaFiles = useCallback(async () => {
    try {
      const response = await getIndividualBeneficiaryRefundMediaFiles(
        refundData.id,
      );
      setMediaFiles(response.data ?? []);
    } catch (error) {
      console.error("Erro ao buscar anexos do reembolso:", error);
    }
  }, [refundData.id]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getIndividualBeneficiaryRefundLogs(
          refundData.id,
        );

        if (response.data?.items) {
          setLogData(response.data.items);
        }
      } catch (error) {
        console.error("Erro ao buscar logs do reembolso:", error);
      }
    };

    fetchData();
    fetchMediaFiles();
  }, [refundData.id, fetchMediaFiles]);

  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return format(date, "Pp", {
      locale:
        i18n.language === "pt-BR"
          ? dateFnsPtBR
          : i18n.language === "en-US"
            ? enUS
            : es,
    }).replaceAll(",", "");
  };

  const handleShowFullLog = (log: string) => {
    setSelectedLogData(log);
    setShowLogModal(true);
  };

  const columns: GridColDef[] = [
    {
      field: "status",
      headerName: t(translationPath + "status") || "Status",
      width: 160,
      renderCell: (params) => {
        return (
          <Chip
            label={params.value?.name ?? "-"}
            color={statusColors[params.value?.code] ?? "default"}
            size="small"
          />
        );
      },
    },
    {
      field: "created",
      headerName: t(translationPath + "created") || "Data de Criação",
      width: 160,
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: "message",
      headerName: t(translationPath + "message") || "Mensagem",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => {
        const message = params.value ?? "-";
        return (
          <Tooltip title={message}>
            <Typography
              variant="body2"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {message}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: "log",
      headerName: t(translationPath + "log_data") || "Dados do Log",
      flex: 1,
      minWidth: 260,
      renderCell: (params) => {
        const log = params.value || "";
        const preview = log.length > 150 ? log.substring(0, 150) + "..." : log;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              width: "100%",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontFamily: "monospace",
                fontSize: "0.75rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                flex: 1,
              }}
            >
              {preview}
            </Typography>
            {log && (
              <Tooltip
                title={
                  t(translationPath + "view_full_log") || "Ver log completo"
                }
              >
                <IconButton
                  size="small"
                  onClick={() => handleShowFullLog(log)}
                  sx={{ padding: 0.5 }}
                >
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ];

  const handleAccordionChange = useCallback(
    (mediaFileId: string) => async (_: unknown, isExpanded: boolean) => {
      setExpandedMediaFileId(isExpanded ? mediaFileId : false);

      if (isExpanded && !mediaFileLogs[mediaFileId]) {
        try {
          const response = await getIndividualBeneficiaryRefundLogs(
            refundData.id,
            mediaFileId,
          );
          setMediaFileLogs((prev) => ({
            ...prev,
            [mediaFileId]: response.data?.items ?? [],
          }));
        } catch (error) {
          console.error("Erro ao buscar logs do anexo:", error);
        }
      }
    },
    [mediaFileLogs, refundData.id],
  );

  const handleRetryMediaFile = useCallback(
    async (mediaFileId: string) => {
      const { success } = await retryMediaFileRequest(mediaFileId);

      if (success) {
        setMessage({
          message: t(translationPath + "media_file_retry_success"),
          type: "success",
        });
        fetchMediaFiles();
      }
    },
    [retryMediaFileRequest, setMessage, t, fetchMediaFiles],
  );

  return (
    <>
      <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
        <DialogActions>
          <Button onClick={onClose}>X</Button>
        </DialogActions>
        <DialogTitle sx={{ paddingY: 0, paddingX: 6, overflow: "hidden" }}>
          {t(translationPath + "title") || "Detalhes do Reembolso"} -{" "}
          {refundData.holderDocument ?? refundData.id}
        </DialogTitle>
        <DialogContent
          sx={{ maxHeight: "80vh", width: "100%", marginTop: 0 }}
        >
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            {t(translationPath + "protocol_logs_title") || "Logs do Protocolo"}
          </Typography>
          <Box sx={{ height: 320, width: "100%" }}>
            <TableList
              columns={columns}
              rows={logData}
              fileName={`refund-logs-${refundData.id}`}
              showSelects={false}
              onRowSelectionModelChange={undefined}
              pageSizeOptions={[5, 10, 25, 50]}
              hideTitle={true}
            />
          </Box>

          {mediaFiles.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                {t(translationPath + "attachments_title") || "Anexos"}
              </Typography>
              {mediaFiles.map((mediaFile) => {
                const canRetry =
                  !mediaFile.integrationId &&
                  mediaFile.integrationStatus?.code === ERRO_CODE;

                return (
                  <Accordion
                    key={mediaFile.id}
                    expanded={expandedMediaFileId === mediaFile.id}
                    onChange={handleAccordionChange(mediaFile.id)}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        gap={2}
                        sx={{ width: "100%" }}
                      >
                        <Typography sx={{ flex: 1 }}>
                          {mediaFile.fileName}
                        </Typography>

                        {mediaFile.integrationId ? (
                          <Chip
                            label={
                              t(translationPath + "attachment_integrated") ||
                              "Integrado"
                            }
                            color="success"
                            size="small"
                          />
                        ) : mediaFile.integrationStatus ? (
                          <Chip
                            label={mediaFile.integrationStatus.name}
                            color={
                              statusColors[mediaFile.integrationStatus.code] ??
                              "default"
                            }
                            size="small"
                          />
                        ) : (
                          <Chip
                            label={
                              t(translationPath + "attachment_pending") ||
                              "Pendente"
                            }
                            color="warning"
                            size="small"
                          />
                        )}

                        <Typography variant="body2" color="text.secondary">
                          {t(translationPath + "attachment_attempts") ||
                            "Tentativas"}
                          : {mediaFile.attemptCount ?? 0}
                        </Typography>

                        {canRetry && (
                          <Button
                            size="small"
                            startIcon={<ReplayIcon fontSize="small" />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRetryMediaFile(mediaFile.id);
                            }}
                          >
                            {t("page.4uBus.logs.refund.retry") ||
                              "Tentar novamente"}
                          </Button>
                        )}
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box sx={{ height: 400, width: "100%" }}>
                        <TableList
                          columns={columns}
                          rows={mediaFileLogs[mediaFile.id] ?? []}
                          fileName={`refund-media-file-logs-${mediaFile.id}`}
                          showSelects={false}
                          onRowSelectionModelChange={undefined}
                          pageSizeOptions={[5, 10, 25]}
                          hideTitle={true}
                        />
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          )}
        </DialogContent>
      </Dialog>
      <Dialog
        open={showLogModal}
        onClose={() => setShowLogModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {t(translationPath + "log_data") || "Dados do Log"}
        </DialogTitle>
        <DialogContent>
          <Typography
            component="pre"
            sx={{
              fontFamily: "monospace",
              fontSize: "0.8rem",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              backgroundColor: (theme) => theme.palette.grey[100],
              padding: 2,
              borderRadius: 1,
              maxHeight: 400,
              overflow: "auto",
            }}
          >
            {selectedLogData}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowLogModal(false)}>
            {t(translationPath + "close") || "Fechar"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RefundDetailsModal;
